(ns yardwerkz.web.messages.inbox
  "Messages inbox: conversation list with inline chat panel."
  (:require
   [uix.core :refer [defui $ use-state use-effect]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.jobs.chat :as chat]
   [yardwerkz.web.ui.layout :as layout]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]))

;; =============================================================================
;; Helpers
;; =============================================================================

(defn- relative-time [iso-str]
  (when iso-str
    (try
      (let [d (js/Date. iso-str)
            now (js/Date.)
            diff-ms (- (.getTime now) (.getTime d))
            diff-min (js/Math.floor (/ diff-ms 60000))
            diff-hr (js/Math.floor (/ diff-min 60))
            diff-day (js/Math.floor (/ diff-hr 24))]
        (cond
          (< diff-min 1) "now"
          (< diff-min 60) (str diff-min "m")
          (< diff-hr 24) (str diff-hr "h")
          (< diff-day 7) (str diff-day "d")
          :else (let [month (inc (.getMonth d))
                      day (.getDate d)]
                  (str month "/" day))))
      (catch :default _ ""))))

;; =============================================================================
;; Conversation Item
;; =============================================================================

(defui conversation-item [{:keys [conv selected? on-click]}]
  (let [unread? (pos? (or (:unread-count conv) 0))]
    ($ :button {:class (str "w-full text-left p-4 border-b border-gray-100 transition-colors "
                            (cond
                              selected? "bg-green-50 border-l-2 border-l-green-500"
                              unread? "bg-blue-50 hover:bg-blue-100"
                              :else "hover:bg-gray-50"))
                :on-click on-click}
       ($ :div {:class "flex items-start space-x-3"}
          ;; Avatar
          ($ :div {:class (str "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 "
                               (if unread? "bg-green-200" "bg-gray-200"))}
             ($ icons/User {:size 20 :class (if unread? "text-green-700" "text-gray-500")}))
          ;; Content
          ($ :div {:class "flex-1 min-w-0"}
             ($ :div {:class "flex items-center justify-between"}
                ($ :p {:class (str "text-sm font-medium truncate "
                                   (if unread? "text-gray-900" "text-gray-700"))}
                   (or (:worker-name conv) (:service-name conv) "Conversation"))
                ($ :span {:class "text-xs text-gray-400 flex-shrink-0 ml-2"}
                   (relative-time (:last-message-at conv))))
             ($ :p {:class (str "text-xs truncate mt-0.5 "
                                (if unread? "text-gray-900 font-medium" "text-gray-500"))}
                (or (:last-message conv) "No messages yet"))
             (when unread?
               ($ :span {:class "inline-block w-2 h-2 bg-green-500 rounded-full mt-1"})))))))

;; =============================================================================
;; Page
;; =============================================================================

(defui inbox-page []
  (let [dispatch (rfx/use-dispatch)
        conversations (rfx/use-sub [::store/conversations])
        loading (rfx/use-sub [::store/conversations-loading])
        [selected-job set-selected] (use-state nil)]

    (use-effect
     (fn []
       (dispatch [::store/fetch-conversations])
       js/undefined)
     [dispatch])

    ($ layout/page-container
       ($ :h1 {:class "text-2xl font-bold text-gray-900 mb-6"} "Messages")
       (if loading
         ($ ui/skeleton-list {:count 4})
         (if (seq conversations)
           ($ :div {:class "grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-4 h-[calc(100vh-12rem)]"}
              ;; Conversation list
              ($ :div {:class "md:col-span-1 border border-gray-200 rounded-xl overflow-y-auto bg-white"}
                 (for [conv conversations]
                   (let [job-id (:job-id conv)]
                     ($ conversation-item {:key job-id
                                           :conv conv
                                           :selected? (= selected-job job-id)
                                           :on-click #(set-selected job-id)}))))

              ;; Chat panel
              ($ :div {:class "md:col-span-2 border border-gray-200 rounded-xl overflow-hidden bg-white"}
                 (if selected-job
                   ($ :div {:class "flex flex-col h-full"}
                      ;; Chat header
                      (let [conv (first (filter #(= (:job-id %) selected-job) conversations))]
                        ($ :div {:class "flex items-center p-4 border-b border-gray-200 bg-white"}
                           ($ :div {:class "w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3"}
                              ($ icons/User {:size 16 :class "text-blue-600"}))
                           ($ :div
                              ($ :p {:class "text-sm font-medium text-gray-900"}
                                 (or (:worker-name conv) "Worker"))
                              ($ :p {:class "text-xs text-gray-500"}
                                 (or (:service-name conv) "Job")))))
                      ;; Chat body
                      ($ :div {:class "flex-1 overflow-hidden"}
                         ($ chat/chat-panel {:job-id selected-job})))
                   ($ :div {:class "flex flex-col items-center justify-center h-full text-gray-400"}
                      ($ icons/MessageSquare {:size 48 :class "mb-3"})
                      ($ :p {:class "text-sm"} "Select a conversation to start chatting")))))
           ($ ui/empty-state
              {:icon ($ icons/MessageSquare {:size 48})
               :title "No messages"
               :description "Messages from your jobs will appear here."}))))))
